import React, { memo, useCallback, useState } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Tooltip,
  IconButton
} from '@mui/material';
import { ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import { useNavigation } from '../../contexts/NavigationProvider';
import { Icon } from '../shared/Icons';
import Logo from './shared/Logo';
import UserAvatar from './shared/UserAvatar';
import NavigationMenu from './shared/NavigationMenu';
import TopBar from './shared/TopBar';
import { useUniversityConfig } from '../../contexts/UniversityConfigProvider';
import { useTracking } from '../../hooks/useTracking';

const NavigationItem = memo<{
  item: any;
  isActive: (path: string) => boolean;
  isCollapsed: boolean;
  isMobile: boolean;
  onMobileClose: () => void;
  onDocMenuOpen: (event: React.MouseEvent<HTMLElement>) => void;
  track?:any
}>(({ item, isActive, isCollapsed, isMobile, onMobileClose, onDocMenuOpen }) => {
  const theme = useTheme();
  const track = useTracking({widgetType:"left_navigation_menu",enableTracking:true,globalTrackingEnabled:true});

      const handleNavigationClick = useCallback(() => {
      if (item.path && !item.isExternal) {
        track(
          'navigation_click',
          'click',
          'NavigationMenuClick',
          {
            navigationPath: item.path,
            menu: item.title
          },
          'raw',
          '2'
        );
      }
      
      if (isMobile) {
        onMobileClose();
      }
    }, [item, track, isMobile, onMobileClose]);

  if (item.subitems) {
    return (
      <ListItem disablePadding>
        <Tooltip title={isCollapsed ? item.title : ''} placement="right" disableHoverListener={!isCollapsed}>
          <ListItemButton
            onClick={onDocMenuOpen}
            sx={{
              borderRadius: 1,
              mb: 0.5,
              justifyContent: isCollapsed ? 'center' : 'flex-start',
              px: isCollapsed ? 1 : 2,
              py: isMobile ? 1.5 : 1,
              '&:hover': {
                backgroundColor: theme.palette.action.hover
              }
            }}
          >
            <ListItemIcon
              sx={{
                color: theme.palette.primary.main,
                minWidth: isCollapsed ? 'auto' : 40,
                justifyContent: 'center'
              }}
            >
              <Icon name={item.iconName} />
            </ListItemIcon>
            {!isCollapsed && (
              <ListItemText
                primary={item.title}
                primaryTypographyProps={{
                  fontSize: 14,
                  fontWeight: 500
                }}
              />
            )}
          </ListItemButton>
        </Tooltip>
      </ListItem>
    );
  }

  if (item.path) {
    return (
      <ListItem disablePadding>
        <Tooltip title={isCollapsed ? item.title : ''} placement="right" disableHoverListener={!isCollapsed}>
          <ListItemButton
            component={item.isExternal ? 'a' : Link}
            {...(item.isExternal
              ? {
                  href: item.path,
                  target: '_blank',
                  rel: 'noopener noreferrer'
                }
              : {
                  to: item.path
                })}
            selected={!item.isExternal && isActive(item.path)}
            onClick={handleNavigationClick} 

            sx={{
              borderRadius: 1,
              mb: 0.5,
              position: 'relative',
              justifyContent: isCollapsed ? 'center' : 'flex-start',
              px: isCollapsed ? 1 : 2,
              py: isMobile ? 1.5 : 1,
              '&:hover': {
                backgroundColor: theme.palette.action.hover
              },
              '&.Mui-selected': {
                backgroundColor: theme.palette.action.selected,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 4,
                  backgroundColor: theme.palette.primary.main,
                  borderRadius: '4px 0 0 4px'
                }
              },
              '&.Mui-selected:hover': {
                backgroundColor: theme.palette.action.selected
              }
            }}
          >
            <ListItemIcon
              sx={{
                color: !item.isExternal && isActive(item.path) ? theme.palette.primary.main : theme.palette.text.secondary,
                minWidth: isCollapsed ? 'auto' : 40,
                justifyContent: 'center'
              }}
            >
              <Icon name={item.iconName} />
            </ListItemIcon>
            {!isCollapsed && (
              <ListItemText
                primary={item.title}
                slotProps={{
                  primary: {
                    sx: {
                      fontSize: 14,
                      fontWeight: !item.isExternal && isActive(item.path) ? 600 : 500,
                      color: !item.isExternal && isActive(item.path) ? theme.palette.primary.main : 'inherit'
                    }
                  }
                }}
              />
            )}
            {!isCollapsed && !item.isExternal && isActive(item.path) && (
              <ListItemIcon
                sx={{
                  color: theme.palette.primary.main,
                  minWidth: 24,
                  justifyContent: 'center',
                  marginLeft: 'auto'
                }}
              >
                <ChevronRightIcon fontSize="small" />
              </ListItemIcon>
            )}
          </ListItemButton>
        </Tooltip>
      </ListItem>
    );
  }

  return null;
});

NavigationItem.displayName = 'NavigationItem';

const SideNavigation: React.FC = memo(() => {
  const theme = useTheme();
    const { config } = useUniversityConfig();
  const track = useTracking({enableTracking:true,globalTrackingEnabled:true});

  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isSideNav, setIsSideNav, mobileOpen, setMobileOpen, navigationRoutes, isCollapsed, setIsCollapsed } = useNavigation();

  const [docMenuAnchor, setDocMenuAnchor] = useState<null | HTMLElement>(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);

  const sidebarWidth = isCollapsed && !isMobile ? 70 : 250;

  const handleDrawerToggle = useCallback((): void => {
    setMobileOpen(!mobileOpen);
  }, [mobileOpen, setMobileOpen]);

  const handleNavPositionChange = useCallback((): void => {
    setIsSideNav(!isSideNav);
    if (mobileOpen) setMobileOpen(false);
  }, [isSideNav, setIsSideNav, mobileOpen, setMobileOpen]);

  const handleCollapseToggle = useCallback((): void => {
    setIsCollapsed(!isCollapsed);
  }, [isCollapsed, setIsCollapsed]);

  const handleDocMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setDocMenuAnchor(event.currentTarget);
  }, []);

  const handleDocMenuClose = useCallback(() => {
    setDocMenuAnchor(null);
  }, []);

  const handleUserMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  }, []);

  const handleUserMenuClose = useCallback(() => {
    setUserMenuAnchor(null);
  }, []);

  const handleMobileClose = useCallback(() => {
    setMobileOpen(false);
  }, [setMobileOpen]);

  const isActive = useCallback(
    (path: string): boolean => {
      const currentPath = location.pathname.split('?')[0].split('#')[0];
      if (path === '/') {
        return currentPath === path;
      }
      return currentPath.startsWith(path) || currentPath === path;
    },
    [location.pathname]
  );

  const DesktopHeader = memo(() => (
    <Box
      sx={{
        marginTop: isCollapsed ? '1px' : '',
        p: '6.5px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: isCollapsed ? 'center' : 'space-between',
        borderBottom: `1px solid ${theme.palette.divider}`,
        minHeight: '63px',
        position: 'relative'
      }}
    >
      <Logo variant={isCollapsed ? 'collapsed' : 'sidebar'} showText={!isCollapsed} />

      {!isCollapsed && (
        <Tooltip title="Collapse sidebar">
          <IconButton
            size="small"
            onClick={handleCollapseToggle}
            sx={{
              color: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.08)'
              }
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
        </Tooltip>
      )}

      {isCollapsed && (
        <Tooltip title="Expand sidebar" placement="bottom">
          <IconButton
            size="small"
            onClick={handleCollapseToggle}
            sx={{
              position: 'absolute',
              right: -15,
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              color: theme.palette.primary.main,
              width: 30,
              height: 30,
              zIndex: 1301,
              '&:hover': {
                backgroundColor: theme.palette.action.hover
              }
            }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  ));

  DesktopHeader.displayName = 'DesktopHeader';


  return (
    <>
      {!isMobile && (
        <TopBar
          title={config?.theme?.title}

          sidebarWidth={sidebarWidth}
          showLogo={false}
          showNavSwitch={true}
          onNavPositionChange={handleNavPositionChange}
          userMenuAnchor={userMenuAnchor}
          onUserMenuOpen={handleUserMenuOpen}
          onUserMenuClose={handleUserMenuClose}
        />
      )}

      {isMobile && (
        <TopBar
          isMobile={true}
          showLogo={true}
          showNavSwitch={false}
          onDrawerToggle={handleDrawerToggle}
          userMenuAnchor={userMenuAnchor}
          onUserMenuOpen={handleUserMenuOpen}
          onUserMenuClose={handleUserMenuClose}
        />
      )}

      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? mobileOpen : true}
        onClose={isMobile ? handleDrawerToggle : undefined}
        sx={{
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: sidebarWidth,
            boxSizing: 'border-box',
            border: 'none',
            zIndex: theme.zIndex.drawer,
            position: 'fixed',
            boxShadow: theme.palette.mode === 'light' ? '0 0 10px rgba(0, 0, 0, 0.05)' : '0 0 10px rgba(0, 0, 0, 0.3)',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.standard
            }),
            overflow: 'visible'
          }
        }}
      >
        {isMobile ? (
          <Box
            sx={{
              width: 250,
              height: '100%',
              backgroundColor: theme.palette.background.paper,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Box
              sx={{
                pt: '64px',
                pb: 1,
                px: 2,
                backgroundColor: theme.palette.background.paper,
                position: 'sticky',
                top: 0,
                zIndex: 1
              }}
            >
         
            </Box>

            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                WebkitOverflowScrolling: 'touch',
                '&::-webkit-scrollbar': {
                  width: '4px'
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent'
                },
                '&::-webkit-scrollbar-thumb': {
                  background: theme.palette.divider,
                  borderRadius: '2px'
                }
              }}
            >
              <List
                component="nav"
                sx={{
                  p: 1,
                  pb: 2
                }}
              >
                {navigationRoutes.map((item: any) => (
                  <NavigationItem
                    key={item.title}
                    item={item}
                    isActive={isActive}
                    isCollapsed={false}
                    isMobile={true}
                    onMobileClose={handleMobileClose}
                    onDocMenuOpen={handleDocMenuOpen}
                    track={track} 

                  />
                ))}
              </List>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              height: '100%',
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              display: 'flex',
              flexDirection: 'column',
              position: 'relative'
            }}
          >
            <DesktopHeader />

            <List
              component="nav"
              sx={{
                p: 1,
                flexGrow: 1,
                overflowY: 'auto'
              }}
            >
              {navigationRoutes.map((item: any) => (
                <NavigationItem
                  key={item.title}
                  item={item}
                  isActive={isActive}
                  isCollapsed={isCollapsed}
                  isMobile={false}
                  onMobileClose={handleMobileClose}
                  onDocMenuOpen={handleDocMenuOpen}
                />
              ))}
            </List>
          </Box>
        )}
      </Drawer>

      <NavigationMenu
        anchorEl={docMenuAnchor}
        open={Boolean(docMenuAnchor)}
        onClose={handleDocMenuClose}
        items={navigationRoutes.find((item: any) => item.subitems)?.subitems || []}
        isActive={isActive}
      />
    </>
  );
});

SideNavigation.displayName = 'SideNavigation';

export default SideNavigation;
